/*  iceberg-ui  */
import {
    Button,
    Flex,
    Modal,
    ModalBody,
    ModalFooter
} from '@auviknetworks/iceberg-ui';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * A modal component for displaying a confirmation dialog.
 *
 * @param {Object} props - The component props.
 * @param {string} [props.dataTestid] - data test id
 * @param {string} [props.dataTrackid] - data track id
 * @param {string} [props.cancelButtonLabel] - The label for the cancel button.
 * @param {React.ReactNode} [props.children] - The content to render in the modal body.
 * @param {string} [props.confirmButtonLabel] - The label for the confirm button.
 * @param {boolean} [props.isOpen=false] - Whether the modal is open or not.
 * @param {boolean} [props.loading=false] - Whether the modal is in a loading state.
 * @param {Function} [props.reject=() => undefined] - The function to call when the confirmation is rejected.
 * @param {Function} props.resolve - The function to call when the confirmation is accepted.
 * @param {string} [props.title] - The title to display in the modal header.
 *
 * @returns {React.ReactElement} The rendered Confirm modal component.
 */
export const Confirm = ({
    'data-testid': dataTestid = 'confirmModal',
    'data-trackid': dataTrackid = 'confirmModal',
    cancelButtonLabel,
    children,
    confirmButtonLabel,
    isOpen = false,
    loading = false,
    reject = () => undefined,
    resolve,
    title
}) => {
    const { t } = useTranslation();

    const handleClose = async (handler) => {
        await handler();
    };

    return (
        <Modal
            aria-labelledby="confirm-action-dialog"
            data-testid={dataTestid}
            onClose={() => handleClose(reject)}
            open={isOpen}
            title={title}
            minWidth={[0, '600px']}
            width={['420px', '600px']}
            margin="18px"
        >
            <ModalBody sx={{ padding: '24px 24px 8px' }}>{children}</ModalBody>

            <ModalFooter>
                <Flex
                    width="100%"
                    justifyContent="space-between"
                    flexWrap="wrap"
                >
                    <Button
                        data-testid={`${dataTestid}CancelBtn`}
                        data-trackid={`${dataTrackid}CancelBtn`}
                        onClick={() => handleClose(reject)}
                        variant="inverted"
                        disabled={!!loading}
                    >
                        {cancelButtonLabel || t('Common.cancel')}
                    </Button>
                    <Button
                        data-testid={`${dataTestid}ConfirmBtn`}
                        data-trackid={`${dataTrackid}ConfirmBtn`}
                        onClick={() => handleClose(resolve)}
                        loading={loading}
                        disabled={!!loading}
                    >
                        {confirmButtonLabel || t('Common.confirm')}
                    </Button>
                </Flex>
            </ModalFooter>
        </Modal>
    );
};
